import React from 'react';
import { Calendar, Clock, Film } from 'lucide-react';
import { formatRuntime } from '../../utils/movieUtils';
import { useLazyTMDB } from '../../hooks/useLazyTMDB';
import { buildPosterSrcSet, tmdbIdOf } from '../../services/tmdbService';
import StreamingServiceIcon from '../streaming/StreamingServiceIcon';

const NowPlaying = ({ 
  currentMovie, 
  movieIndex, 
  totalMovies, 
  onPrevious, 
  onNext, 
  onOpenModal,
  timezone,
  convertTimeToViewerTimezone,
  getStreamingServiceById
}) => {
  // Handle both scheduled movies (with nested movie data) and regular movies (direct data)
  const movieData = currentMovie?.movie || currentMovie;
  
  // Use lazy loading for TMDB data (skip for placeholder movies)
  const { tmdbData, loading: tmdbLoading, elementRef } = useLazyTMDB(
    movieData?.isPlaceholder ? null : movieData?.title, 
    movieData?.isPlaceholder ? null : movieData?.year,
    // Load immediately since this is the "now playing" movie; by id when known
    { immediate: true, tmdbId: movieData?.isPlaceholder ? null : tmdbIdOf(movieData) }
  );

  if (!currentMovie) {
    return (
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl p-12 text-center">
        <div className="text-6xl mb-4">🎬</div>
        <h3 className="text-lg font-bold text-orange-300 mb-2">No Movies Available</h3>
        <p className="text-orange-200">
          There are no movies with valid statuses. Check the collection or come back later!
        </p>
      </div>
    );
  }

  // Handle both scheduled movies (with date/timeSlot) and regular movies (from collection)
  const isScheduledMovie = currentMovie.date && currentMovie.timeSlot;
  let movieDateTime, movieEndTime, isCurrentlyPlaying;
  
  if (isScheduledMovie) {
    // This is a scheduled movie with date and time slot
    movieDateTime = new Date(currentMovie.date);
    movieDateTime.setHours(currentMovie.timeSlot.adjustedHour, 0, 0, 0);
    movieEndTime = new Date(movieDateTime);
    movieEndTime.setMinutes(movieEndTime.getMinutes() + movieData.runtime);
    const now = new Date();
    isCurrentlyPlaying = now >= movieDateTime && now < movieEndTime;
  } else {
    // This is a regular movie from the collection - no specific time
    movieDateTime = null;
    movieEndTime = null;
    isCurrentlyPlaying = false;
  }

  return (
    <div ref={elementRef} className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl overflow-hidden">
      <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-orange-300 flex items-center gap-2">
              {isCurrentlyPlaying ? '🎬 Now Playing' : 
               isScheduledMovie ? '📅 Coming Up' : '🎬 Movie Collection'}
            </h2>
            <p className="text-orange-200 text-sm">
              Movie {movieIndex + 1} of {totalMovies}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onPrevious}
              disabled={movieIndex === 0}
              className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              ← Previous
            </button>
            <button
              onClick={onNext}
              disabled={movieIndex === totalMovies - 1}
              className="bg-gray-600 hover:bg-gray-500 disabled:bg-gray-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-8">
        <div className="flex items-center gap-6 mb-6">
          {/* Movie Poster or Fallback */}
          <div 
            className="relative w-32 h-48 rounded-lg border-4 border-orange-600 shadow-lg cursor-pointer hover:border-yellow-400 transition-colors overflow-hidden"
            onClick={() => onOpenModal(currentMovie)}
            title="Click for movie details"
          >
            {tmdbData?.posterUrl ? (
              <>
                <img
                  src={tmdbData.posterUrl}
                  srcSet={buildPosterSrcSet(tmdbData.posterUrl, [154, 185, 342, 500])}
                  sizes="128px"
                  alt={`${movieData.title} poster`}
                  className="w-full h-full object-cover"
                  decoding="async"
                />
                {/* TMDB Rating Badge */}
                {tmdbData.rating && (
                  <div className="absolute top-2 right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                    ⭐ {tmdbData.rating.toFixed(1)}
                  </div>
                )}
                {/* Loading overlay */}
                {tmdbLoading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="text-white text-xs">Loading...</div>
                  </div>
                )}
              </>
            ) : (
              <div className={`w-full h-full ${movieData.color} flex items-center justify-center`}>
                {tmdbLoading ? (
                  <div className="text-white text-center">
                    <Film className="w-8 h-8 mx-auto mb-2 animate-pulse" />
                    <div className="text-xs">Loading poster...</div>
                  </div>
                ) : (
                  <span className="text-white text-xl font-bold text-center px-2 leading-tight">
                    {movieData.title.split(' ').map(word => word[0]).join('').slice(0, 4)}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <h1 
              className="text-3xl font-bold text-orange-100 mb-2 cursor-pointer hover:text-yellow-300 transition-colors"
              onClick={() => onOpenModal(currentMovie)}
              title="Click for movie details"
            >
              {movieData.title}
            </h1>
            <div className="text-orange-300 text-lg mb-4">
              ({tmdbData?.releaseDate ? new Date(tmdbData.releaseDate).getFullYear() : movieData.year}) • 
              {formatRuntime(tmdbData?.runtime || movieData.runtime)}
              {tmdbData?.genres && tmdbData.genres.length > 0 && (
                <span> • {tmdbData.genres[0]}</span>
              )}
            </div>
            
            {/* Director and Stars */}
            {tmdbData && (tmdbData.director || tmdbData.stars?.length > 0) && (
              <div className="text-sm text-gray-300 mb-4">
                {tmdbData.director && (
                  <div className="mb-1">
                    <span className="text-orange-400 font-semibold">Director:</span> {tmdbData.director}
                  </div>
                )}
                {tmdbData.stars?.length > 0 && (
                  <div>
                    <span className="text-orange-400 font-semibold">Stars:</span> {tmdbData.stars.slice(0, 3).join(', ')}
                  </div>
                )}
              </div>
            )}
            
            {isScheduledMovie && (
              <div className="bg-gray-900 border border-orange-500 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={16} className="text-orange-400" />
                  <span className="text-orange-200 font-bold">
                    {movieDateTime.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-orange-400" />
                  <span className="text-orange-200 font-bold">
                    {convertTimeToViewerTimezone ? convertTimeToViewerTimezone(currentMovie.timeSlot.time) : currentMovie.timeSlot.time} - {movieEndTime.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true 
                    })} {timezone}
                  </span>
                </div>
              
                {/* Preferred Streaming Service */}
                {movieData.preferredStreamingService && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-orange-400 font-bold text-sm">📺 Available on:</span>
                    {(() => {
                      const service = getStreamingServiceById(movieData.preferredStreamingService);
                      return service ? (
                        <div className="bg-gray-800 rounded-lg px-3 py-2 border border-orange-400">
                          <StreamingServiceIcon 
                            service={service} 
                            size="md" 
                            showName={true}
                            className="text-orange-200"
                          />
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
                
                {isCurrentlyPlaying && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold inline-block">
                    🔴 LIVE - Currently Playing
                  </div>
                )}
              </div>
            )}
            
            {/* Quick Join Button */}
            <button
              onClick={() => onOpenModal(currentMovie)}
              className="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-bold transition-colors"
            >
              📺 View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowPlaying;

